import {
  ExtendedAccommodationCatalogPropertyFragment,
  ExtendedAccommodationContentSearchPayload,
  ScoringPriorityField,
  useSearchExtendedAccommodationPropertiesLazyQuery,
  useSearchExtendedAccommodationPropertiesQuery,
} from '@graphql/generated';
import { useEventInfo } from '@shared/utils/eventInfo';
import { useCheckInCheckoutDates } from '../useCheckInCheckoutDates';
import { useCallback, useContext, useMemo } from 'react';
import { addError } from '@shared/utils/logger';
import { getCalculatedPrice } from '@shared/utils/accommodationPrice';
import { useMinimumPercentageOffToDisplay } from '../useMinimumPercentageOffToDisplay';
import { useLocaleContext } from '@shared/core/i18n/LocaleContext';
import { distanceInMiles } from '@shared/utils/distanceInMiles';
import { isValidCoordinates } from '@shared/core/places';
import { AnalyticsContext } from '@shared/core';
import { parseNetworkErrorDetails } from '@shared/utils/parseNetworkErrorDetails';

export const HOTELS_SEARCH_DEFAULT_RADIUS_KM = 5;

/**
 *
 * @param scoringPriorityFields - array of ScoringPriorityField to fetch properties
 * @param skipOnInitialLoad - Skip fetch on initial load or any amplitude flag off
 */
export const useSearchExtendedAccommodationProperties = (
  location: { latitude: number; longitude: number },
  scoringPriorityFields: ScoringPriorityField[],
  skipOnInitialLoad = false,
  context: string = 'searchExtendedAccommodationProperties',
) => {
  const analytics = useContext(AnalyticsContext);
  const { eventInfo, loading: loadingEventInfo } = useEventInfo();
  const eventId = eventInfo?.eventId;
  const eventDate = eventInfo?.finalizedDate?.dateString || undefined;

  const { checkIn: checkInDate, checkOut: checkOutDate } = useCheckInCheckoutDates({ eventDate });

  const payload: ExtendedAccommodationContentSearchPayload | undefined = useMemo(() => {
    if (loadingEventInfo || !eventId) {
      return undefined;
    }
    return {
      location,
      metadata: {
        eventId,
      },
      radiusKM: HOTELS_SEARCH_DEFAULT_RADIUS_KM,
      occupancies: {
        occupancies: [
          {
            adults: 2,
            childrenAges: [],
          },
        ],
        unitCount: 1,
      },
      stayDates: {
        checkIn: {
          high: checkInDate,
          low: checkInDate,
        },
        checkOut: {
          high: checkOutDate,
          low: checkOutDate,
        },
      },
      scoringPriorityFields,
    };
  }, [location, checkInDate, checkOutDate, eventId, loadingEventInfo, scoringPriorityFields]);

  const { data, loading, called, error } = useSearchExtendedAccommodationPropertiesQuery({
    batchMode: 'fast',
    variables: {
      payload: payload!,
    },
    skip: skipOnInitialLoad || !payload,
    onError(error) {
      const { errorExplanation, statusCode, isTimeout } = parseNetworkErrorDetails(error);
      addError(errorExplanation, {
        source: 'Pricing api failed',
        context,
        errorMessage: error.message,
        checkInDate,
        checkOutDate,
        metadata: payload?.metadata,
        statusCode,
        isTimeout,
        originalError: error,
      });
    },
    onCompleted(data) {
      const properties = data.searchExtendedAccommodationProperties.properties;
      analytics.track({
        category: 'accommodations',
        action: `TravelButtonInteracted`,
        label: 'searchExtendedAccommodationProperties',
        extraInfo: {
          name: `searchExtendedAccommodationProperties`,
          context,
          checkInDate,
          checkOutDate,
          metadata: payload?.metadata,
          hotelsCount: properties.length,
        },
      });
    },
  });

  const [
    getSearchExtendedAccommodationProperties,
    {
      data: lazyData,
      loading: loadingGetSearchExtendedAccommodationProperties,
      called: calledGetSearchExtendedAccommodationProperties,
      error: errorGetSearchExtendedAccommodationProperties,
    },
  ] = useSearchExtendedAccommodationPropertiesLazyQuery();

  const fetchSearchExtendedAccommodationProperties = useCallback(
    (scoringPriorityFields: Array<ScoringPriorityField>, radiusKM: number) => {
      if (!scoringPriorityFields.length || !payload) return;
      getSearchExtendedAccommodationProperties({ variables: { payload: { ...payload, scoringPriorityFields, radiusKM } } });
    },
    [payload, getSearchExtendedAccommodationProperties],
  );

  return {
    data: data || lazyData,
    loading: loading || loadingGetSearchExtendedAccommodationProperties,
    called: called || calledGetSearchExtendedAccommodationProperties,
    error: error || errorGetSearchExtendedAccommodationProperties,
    checkInDate,
    checkOutDate,
    fetchSearchExtendedAccommodationProperties,
  };
};

export type AccommodationPropertyWithCalculatedPrice = Omit<ExtendedAccommodationCatalogPropertyFragment, 'location' | 'pricingInfo'> & {
  lat: number;
  lng: number;
  distance: number | null;
  price?: string;
  originalPrice?: number;
  strikeOutPrice?: string;
  percentageOff?: number;
  minorUnits?: number;
  accommodationPlaceId?: string;
};

/**
 * Returns accommodation properties with calculated price and location info.
 */
export const useAccommodationPropertiesWithCalculatedPrice = (
  properties: ExtendedAccommodationCatalogPropertyFragment[] = [],
  destination?: google.maps.LatLngLiteral,
): AccommodationPropertyWithCalculatedPrice[] => {
  const locale = useLocaleContext();
  const { minimumPercentageOffToDisplayValue } = useMinimumPercentageOffToDisplay();

  // Memoize for performance
  return useMemo(() => {
    if (!properties.length) return [];

    type ValidProperty = ExtendedAccommodationCatalogPropertyFragment & {
      location: { latitude: number; longitude: number };
      pricingInfo: NonNullable<ExtendedAccommodationCatalogPropertyFragment['pricingInfo']>;
    };

    return properties
      .filter((p): p is ValidProperty => isValidCoordinates(p.location))
      .map(property => {
        const calculatedPrice = getCalculatedPrice(property, locale, minimumPercentageOffToDisplayValue, property?.joyPlaceId);
        const { latitude, longitude } = property.location;
        const distance = destination ? distanceInMiles({ lat: latitude, lng: longitude }, destination) : null;
        const { price, strikeOutPrice, percentageOff, minorUnits } = calculatedPrice;
        // Omit location and pricingInfo from property if not needed
        const { location, pricingInfo, ...rest } = property;
        const originalPrice = pricingInfo?.nightlyBaseRate?.valueInMinorUnits;
        return {
          ...rest,
          lat: latitude,
          lng: longitude,
          distance,
          price,
          originalPrice,
          strikeOutPrice,
          percentageOff,
          minorUnits,
        };
      })
      .sort((a, b) => b.score - a.score);
  }, [properties, destination, locale, minimumPercentageOffToDisplayValue]);
};
